import {
  AsyncSelect,
  SubText,
  Text,
  type IAsyncSelectProps,
} from '@procurify/ui'
import { useSelector } from 'react-redux'
import {
  type SingleValueProps,
  type OptionProps,
  components as ReactSelectComponents,
} from 'react-select'

import { ContentTypeAppLabels } from '@webapp/core/entities/internal/enums/ContentTypeAppLabelEnum'
import { ContentTypeModels } from '@webapp/core/entities/internal/enums/ContentTypeModelEnum'
import {
  makeSelectContentTypesByModel,
  makeSelectUser,
} from '@webapp/core/stores/global'
import { useProcurifyIntl } from '@webapp/hooks'
import { approverRepository } from '@webapp/modules/expense/data-access/repositories/ApproverRepository'
import { RequestableApproverOption } from '@webapp/modules/expense/entities/RequestableApproverOption'
import messages from './messages'

export type NextApproverSelectValue = RequestableApproverOption

export interface INextApproverSelectProps extends IAsyncSelectProps<NextApproverSelectValue> {
  departmentId: Nullable<number>
  accountCodeIds: number[]
  amount: number
}

const ApproverSelectOptionLabel = ({
  approver,
  userId,
}: {
  approver: NextApproverSelectValue
  userId: number
}) => {
  const intl = useProcurifyIntl()

  return (
    <>
      <Text autoMargin={false}>
        {approver.displayLabel}
        {approver.userId === userId
          ? ` (${intl.formatMessage(messages.selfApprovalLabel)})`
          : ''}
        {!!approver.approvalDelegatee &&
          ` --> ${intl.formatMessage(messages.delegatedToLabel, {
            user: approver.approvalDelegatee.fullName,
          })}
          ${
            approver.approvalDelegatee.id === userId
              ? ` (${intl.formatMessage(messages.selfApprovalLabel)})`
              : ''
          }`}
      </Text>
      {!!approver.groupName && (
        <SubText color='textSecondary'>{approver.groupName}</SubText>
      )}
    </>
  )
}

export const SingleValue = (
  props: SingleValueProps<NextApproverSelectValue, false> & { userId: number }
) => {
  const { userId, ...otherProps } = props
  return (
    <ReactSelectComponents.SingleValue
      {...otherProps}
      className='approval-chain-approver-single-value'
    >
      <ApproverSelectOptionLabel approver={props.data} userId={userId} />
    </ReactSelectComponents.SingleValue>
  )
}

export const Option = (
  props: OptionProps<NextApproverSelectValue, false> & { userId: number }
) => {
  const { userId, ...otherProps } = props

  return (
    <ReactSelectComponents.Option {...otherProps}>
      <ApproverSelectOptionLabel approver={props.data} userId={userId} />
    </ReactSelectComponents.Option>
  )
}

export const NextApproverSelect = ({
  departmentId,
  accountCodeIds,
  amount,
  ...props
}: INextApproverSelectProps) => {
  const intl = useProcurifyIntl()

  const expenseContentType = useSelector(
    makeSelectContentTypesByModel(
      ContentTypeAppLabels.PROCURIFY,
      ContentTypeModels.EXPENSEREPORT
    )
  )
  const baseContentTypeId = expenseContentType?.id
  const userId = useSelector(makeSelectUser()).id

  const loadOptions: IAsyncSelectProps<NextApproverSelectValue>['loadOptions'] =
    async (_inputValue, _pagination, callback) => {
      if (!baseContentTypeId || departmentId === null) {
        callback({
          options: [],
          pagination: { hasMore: false, page: 1 },
        })
        return
      }

      try {
        const approvers = await approverRepository.listRequestableApprovers({
          baseContentTypeId,
          department: departmentId,
          accountCodeIds,
          customFields: [],
          amount,
        })
        callback({
          options: approvers.map(
            (approver) => new RequestableApproverOption(approver)
          ),
          pagination: { hasMore: false, page: 1 },
        })
      } catch {
        callback({
          options: [],
          pagination: { hasMore: false, page: 1 },
        })
      }
    }

  return (
    <AsyncSelect<NextApproverSelectValue>
      components={{
        SingleValue: (componentProps) => (
          <SingleValue {...componentProps} userId={userId} />
        ),
        Option: (componentProps) => (
          <Option {...componentProps} userId={userId} />
        ),
      }}
      cacheOptions={true}
      clearCacheOnMenuClose
      loadOptions={loadOptions}
      placeholder={intl.formatMessage(messages.placeholderText)}
      getOptionValue={(option) => option.id.toString()}
      isDisabled={!departmentId}
      {...props}
    />
  )
}
